import { FiltersOperator } from 'common/api/resources/fieldFilters';

export { FiltersOperator };

export enum AlphanumericConditions {
  isEmpty = 'isEmpty',
  isNotEmpty = 'isNotEmpty',
  contains = 'contains',
  notContains = 'notContains',
}

export enum DropdownConditions {
  is = 'is',
  isNot = 'isNot',
  isDropdownSet = 'isDropdownSet',
  isDropdownNotSet = 'isDropdownNotSet',
}

// Special condition only for Status field
export enum StatusConditions {
  isOfType = 'isOfType',
}

export enum MultiselectConditions {
  isOneOf = 'isOneOf',
  isNotOneOf = 'isNotOneOf',
  isMultiselectSet = 'isMultiselectSet',
  isMultiselectNotSet = 'isMultiselectNotSet',
}

export enum DateConditions {
  isDateSet = 'isDateSet',
  isDateNotSet = 'isDateNotSet',
  // TODO: Add support to these conditions by adding the appropriate input type
  // isBefore = 'isBefore',
  // isAfter = 'isAfter',
  isBetween = 'isBetween',
}

export enum BooleanConditions {
  isTrue = 'isTrue',
  isFalse = 'isFalse',
}

export enum NumericConditions {
  equals = 'equals',
  doesNotEqual = 'doesNotEqual',
  greaterThan = 'greaterThan',
  lessThan = 'lessThan',
}

export type CustomFieldFilterConditions =
  | DropdownConditions
  | StatusConditions
  | MultiselectConditions
  | AlphanumericConditions
  | BooleanConditions
  | DateConditions
  | NumericConditions;

export enum InputTypes {
  text = 'text',
  number = 'number',
  dropdown = 'dropdown',
  multiselect = 'multiselect',
  dateRange = 'dateRange',
  statusType = 'statusType',
}

export type ConditionType =
  | 'alphanumeric'
  | 'numeric'
  | 'dropdown'
  | 'multiselect'
  | 'boolean'
  | 'date';

const ConditionMap: Record<
  CustomFieldFilterConditions,
  {
    condition: CustomFieldFilterConditions;
    input: InputTypes | null;
    label: string;
    type: ConditionType;
  }
> = {
  [AlphanumericConditions.isEmpty]: {
    condition: AlphanumericConditions.isEmpty,
    input: null,
    label: 'Is empty',
    type: 'alphanumeric',
  },
  [AlphanumericConditions.isNotEmpty]: {
    condition: AlphanumericConditions.isNotEmpty,
    input: null,
    label: 'Is not empty',
    type: 'alphanumeric',
  },
  [AlphanumericConditions.contains]: {
    condition: AlphanumericConditions.contains,
    input: InputTypes.text,
    label: 'Contains',
    type: 'alphanumeric',
  },
  [AlphanumericConditions.notContains]: {
    condition: AlphanumericConditions.notContains,
    input: InputTypes.text,
    label: 'Does not contain',
    type: 'alphanumeric',
  },
  [NumericConditions.doesNotEqual]: {
    condition: NumericConditions.doesNotEqual,
    input: InputTypes.number,
    label: 'Does not equal',
    type: 'numeric',
  },
  [NumericConditions.equals]: {
    condition: NumericConditions.equals,
    input: InputTypes.number,
    label: 'Equals',
    type: 'numeric',
  },
  [NumericConditions.greaterThan]: {
    condition: NumericConditions.greaterThan,
    input: InputTypes.number,
    label: 'Greater than',
    type: 'numeric',
  },
  [NumericConditions.lessThan]: {
    condition: NumericConditions.lessThan,
    input: InputTypes.number,
    label: 'Less than',
    type: 'numeric',
  },
  [DropdownConditions.is]: {
    condition: DropdownConditions.is,
    input: InputTypes.dropdown,
    label: 'Is',
    type: 'dropdown',
  },
  [DropdownConditions.isNot]: {
    condition: DropdownConditions.isNot,
    input: InputTypes.dropdown,
    label: 'Is not',
    type: 'dropdown',
  },
  [DropdownConditions.isDropdownSet]: {
    condition: DropdownConditions.isDropdownSet,
    input: null,
    label: 'Is set',
    type: 'dropdown',
  },
  [DropdownConditions.isDropdownNotSet]: {
    condition: DropdownConditions.isDropdownNotSet,
    input: null,
    label: 'Is not set',
    type: 'dropdown',
  },
  [StatusConditions.isOfType]: {
    condition: StatusConditions.isOfType,
    input: InputTypes.statusType,
    label: 'Is of type',
    type: 'multiselect',
  },
  [MultiselectConditions.isOneOf]: {
    condition: MultiselectConditions.isOneOf,
    input: InputTypes.multiselect,
    label: 'Is one of',
    type: 'multiselect',
  },
  [MultiselectConditions.isNotOneOf]: {
    condition: MultiselectConditions.isNotOneOf,
    input: InputTypes.multiselect,
    label: 'Is not one of',
    type: 'multiselect',
  },
  [MultiselectConditions.isMultiselectSet]: {
    condition: MultiselectConditions.isMultiselectSet,
    input: null,
    label: 'Is set',
    type: 'multiselect',
  },
  [MultiselectConditions.isMultiselectNotSet]: {
    condition: MultiselectConditions.isMultiselectNotSet,
    input: null,
    label: 'Is not set',
    type: 'multiselect',
  },
  [BooleanConditions.isTrue]: {
    condition: BooleanConditions.isTrue,
    input: null,
    label: 'Is true',
    type: 'boolean',
  },
  [BooleanConditions.isFalse]: {
    condition: BooleanConditions.isFalse,
    input: null,
    label: 'Is false',
    type: 'boolean',
  },
  [DateConditions.isDateSet]: {
    condition: DateConditions.isDateSet,
    input: null,
    label: 'Is set',
    type: 'date',
  },
  [DateConditions.isDateNotSet]: {
    condition: DateConditions.isDateNotSet,
    input: null,
    label: 'Is not set',
    type: 'date',
  },
  // TODO: Add support to these conditions by adding the appropriate input type
  // [DateConditions.isBefore]: {
  //   condition: DateConditions.isBefore,
  //   input: InputTypes.text,
  //   label: 'Is before',
  //   type: 'date',
  // },
  // [DateConditions.isAfter]: {
  //   condition: DateConditions.isAfter,
  //   input: InputTypes.text,
  //   label: 'Is after',
  //   type: 'date',
  // },
  [DateConditions.isBetween]: {
    condition: DateConditions.isBetween,
    input: InputTypes.dateRange,
    label: 'Is between',
    type: 'date',
  },
};

export const getConditionInputType = (condition: CustomFieldFilterConditions) => {
  return ConditionMap[condition]?.input ?? null;
};

export const getConditionsForType = (type: ConditionType) => {
  return Object.values(ConditionMap).filter((condition) => condition.type === type);
};
